/**
 * TextureManager — Central API for applying SVG-based textures to Fabric.js objects.
 *
 * Loads SVG files as Images, creates fabric.Pattern objects, and caches them
 * for reuse. Objects store their material in `data.material` and their type
 * in `data.objectType`; the manager resolves both to the correct pattern.
 *
 * Public API:
 *   textureManager.getPattern(objectType, material)  → Promise<fabric.Pattern>
 *   textureManager.applyTexture(fabricObject, canvas) → Promise<void>
 *   textureManager.applySolidColor(fabricObject, color, canvas) → void
 *   textureManager.clearCache()                        → void
 */

import * as fabric from 'fabric';
import { getTextureSvgUrl, supportsTexture, LINE_ONLY_TYPES } from './materialMappings';

// Pattern cache: keyed by SVG URL
const patternCache = new Map();

// Image loading cache: keyed by SVG URL, value is Promise<HTMLImageElement>
const imageLoadCache = new Map();

/**
 * Load an image from a URL. Returns a cached promise if already loading/loaded.
 *
 * @param {string} url - The image URL to load
 * @returns {Promise<HTMLImageElement>}
 */
function loadImage(url) {
  if (imageLoadCache.has(url)) {
    return imageLoadCache.get(url);
  }

  const promise = new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load texture: ${url}`));
    img.src = url;
  });

  imageLoadCache.set(url, promise);
  return promise;
}

/**
 * #720: Der Mittellinien-Weg wird als Stroke-Band gezeichnet (`fill: null`, das
 * Band IST der Stroke). Material und Farbe gehören deshalb auf den **Stroke** —
 * eine Füllung würde den offenen Pfad schließen und die Sichel zwischen Kurve
 * und Sehne einfärben (sichtbarer Fehler bei schmalen Wegen). Erkennung am
 * Datenmodell (`shapeType === 'centerline'`), nicht am Typ, damit Legacy-Wege
 * (geschlossene Fläche) weiter über `fill` texturiert werden.
 *
 * @param {fabric.Object} fabricObject
 * @returns {boolean}
 */
function isStrokeRibbon(fabricObject) {
  return fabricObject?.data?.shapeType === 'centerline';
}

/**
 * Create a fabric.Pattern from a loaded image element.
 *
 * @param {HTMLImageElement} img - The loaded image
 * @returns {fabric.Pattern}
 */
function createPattern(img) {
  return new fabric.Pattern({
    source: img,
    repeat: 'repeat',
  });
}

/**
 * Get a fabric.Pattern for a given object type and material.
 * Returns from cache if available, otherwise loads the SVG and creates the pattern.
 *
 * @param {string} objectType - Garden object type ID (e.g., 'weg', 'terrasse')
 * @param {string|null} material - Material value (e.g., 'kies'), or null for default
 * @returns {Promise<fabric.Pattern|null>} The pattern, or null if no texture available
 */
async function getPattern(objectType, material) {
  if (LINE_ONLY_TYPES.has(objectType)) {
    return null;
  }

  const svgUrl = getTextureSvgUrl(objectType, material);
  if (!svgUrl) {
    return null;
  }

  // Return cached pattern if available
  if (patternCache.has(svgUrl)) {
    return patternCache.get(svgUrl);
  }

  // Load image and create pattern
  const img = await loadImage(svgUrl);
  const pattern = createPattern(img);
  patternCache.set(svgUrl, pattern);
  return pattern;
}

/**
 * Apply the correct texture to a Fabric.js object based on its data properties.
 * Reads `data.objectType` and `data.material` from the object.
 *
 * If `data.material` is 'custom' and `data.color` is set, applies solid color instead.
 *
 * @param {fabric.Object} fabricObject - The Fabric.js object to texture
 * @param {fabric.Canvas} canvas - The canvas (for triggering re-render)
 * @returns {Promise<boolean>} true if texture was applied, false otherwise
 */
async function applyTexture(fabricObject, canvas) {
  const data = fabricObject.data;
  const objectType = data?.objectType || data?.gardenObjectType;
  if (!objectType) {
    return false;
  }

  // Don't apply textures to line-only types
  if (!supportsTexture(objectType)) {
    return false;
  }

  // If custom color mode, apply solid color
  if ((data.useCustomColor || data.material === 'custom') && data.color) {
    fabricObject.set(
      isStrokeRibbon(fabricObject) ? { stroke: data.color, fill: null } : { fill: data.color }
    );
    if (canvas) {
      canvas.requestRenderAll();
    }
    return true;
  }

  const material = data.material || null;
  const pattern = await getPattern(objectType, material);

  if (!pattern) {
    return false;
  }

  fabricObject.set(
    isStrokeRibbon(fabricObject) ? { stroke: pattern, fill: null } : { fill: pattern }
  );
  // Disable object caching so the pattern renders correctly
  fabricObject.set({ objectCaching: false });
  if (canvas) {
    canvas.requestRenderAll();
  }
  return true;
}

/**
 * Apply a solid color fill to a Fabric.js object (for "Benutzerdefiniert" mode).
 *
 * @param {fabric.Object} fabricObject - The Fabric.js object
 * @param {string} color - CSS color value (e.g., '#FF0000')
 * @param {fabric.Canvas} canvas - The canvas (for triggering re-render)
 */
function applySolidColor(fabricObject, color, canvas) {
  fabricObject.set(
    isStrokeRibbon(fabricObject) ? { stroke: color, fill: null } : { fill: color }
  );
  // Re-enable object caching for solid colors
  fabricObject.set({ objectCaching: true });
  if (canvas) {
    canvas.requestRenderAll();
  }
}

/**
 * Clear all cached patterns and images.
 * Useful for testing or when switching themes.
 */
function clearCache() {
  patternCache.clear();
  imageLoadCache.clear();
}

export const textureManager = {
  getPattern,
  applyTexture,
  applySolidColor,
  clearCache,
};

// Also export individual functions for tree-shaking
export { getPattern, applyTexture, applySolidColor, clearCache };
